import React, { useState, useEffect, useRef } from 'react'
import { domains } from 'lib/config'

const { CDN_DOMAIN } = domains

export type NotificationColor =
  | 'question'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'

export interface NotificationProps {
  autoDismissTimeout?: number
  children?: React.ReactNode
  clearBackground?: boolean
  color: NotificationColor
  dismissible?: boolean
  dismissed?: boolean
  fade?: boolean
  id?: number
  onDismiss?: () => void
  shadow?: boolean
  showIcon?: boolean
  showIdon?: boolean
  text?: string
}

const PNG_ICONS: Record<NotificationColor | 'close', string> = {
  question: `${CDN_DOMAIN}/assets/img/question_icon_32px.png`,
  info: `${CDN_DOMAIN}/assets/img/info_icon_32px.png`,
  success: `${CDN_DOMAIN}/assets/img/success_icon_32px.png`,
  warning: `${CDN_DOMAIN}/assets/img/yield_icon_32px.png`,
  danger: `${CDN_DOMAIN}/assets/img/warning_icon_32px.png`,
  close: `${CDN_DOMAIN}/assets/img/cross_icon.png`,
}

const Notification: React.FC<NotificationProps> = ({
  autoDismissTimeout = 0,
  children,
  clearBackground = false,
  color,
  dismissible = false,
  dismissed: initialDismissed = false,
  fade = true,
  onDismiss,
  shadow = false,
  showIcon = true,
  text,
}) => {
  const [show, setShow] = useState(true)
  const [dismissed, setDismissed] = useState(initialDismissed)
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  const onDismissRef = useRef(onDismiss)

  // Sync dismissed state with initialDismissed prop
  // Only update if the prop actually changed to avoid infinite loops
  useEffect(() => {
    setDismissed(prevDismissed => {
      // Only update if the prop value is different from current state
      return initialDismissed !== prevDismissed
        ? initialDismissed
        : prevDismissed
    })
  }, [initialDismissed])

  // Keep the ref updated with the latest onDismiss callback
  // Use a ref to avoid triggering re-renders when onDismiss changes
  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    isMountedRef.current = true

    if (dismissible && autoDismissTimeout > 0 && !dismissed) {
      const timeout = setTimeout(() => {
        if (isMountedRef.current) {
          setDismissed(true)
          if (onDismissRef.current) {
            onDismissRef.current()
          }
        }
      }, autoDismissTimeout)

      dismissTimeoutRef.current = timeout
    }

    return () => {
      isMountedRef.current = false
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current)
        dismissTimeoutRef.current = null
      }
    }
  }, [dismissible, autoDismissTimeout, dismissed])

  const handleDismiss = () => {
    // Clear any existing timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current)
      dismissTimeoutRef.current = null
    }
    // Immediately remove from DOM and call onDismiss to remove from Redux store
    setDismissed(true)
    setShow(false)
    if (onDismissRef.current) {
      onDismissRef.current()
    }
  }

  const content = children || text

  if (dismissed) return null

  const icon = color
  const displayColor = clearBackground
    ? 'none'
    : color === 'question'
    ? 'info'
    : color

  return (
    <div
      className={`notification-wrapper ${fade ? 'fade' : ''} ${
        show ? 'show' : ''
      }`}
    >
      <div
        className={`alert alert-${displayColor} notification-alert`}
        style={{}}
      >
        {showIcon && <div className="img" />}
        <div className="message">{content}</div>
        {dismissible && (
          <div className="dismiss-container" onClick={handleDismiss}>
            <div className="dismiss">
              <span className="dismiss-link">Dismiss</span>&nbsp;&nbsp;
              <i className="fas fa-times" />
            </div>
          </div>
        )}
      </div>

      <style>
        {`
          .notification-wrapper {
            ${
              shadow
                ? 'box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);'
                : ''
            }
          }

          .notification-alert {
            display: flex;
            align-content: flex-start;
            padding: 1.12rem 1.5rem;
            font-size: 1.125rem;
            line-height: 1.5rem;
            border: none;
          }

          .notification-alert > * {
            display: flex;
            align-content: center;
            color: #1f1f1f !important;
          }

          .notification-alert .img {
            background-image: url(${PNG_ICONS[icon]});
            background-repeat: no-repeat;
            width: 2rem;
            height: 2rem;
            padding-right: 3.4rem;
          }

          .notification-alert .message {
            flex-grow: 1;
            margin-top: 0.1875rem;
            ${dismissible ? 'padding-right: 3.75rem;' : ''}
            display: block;
          }

          .notification-alert .dismiss-container {
            display: flex;
            margin-top: 3px;
            white-space: nowrap;
            cursor: pointer;
          }

          .notification-alert .dismiss-link {
            font-weight: bold;
            text-decoration: underline;
          }

          .notification-alert .fa-times {
            display: inline-block;
            vertical-align: middle;
          }
        `}
      </style>
    </div>
  )
}

export default Notification
